/**
 * Extracts the `next` query parameter from a raw query string, handling both
 * properly percent-encoded and unencoded URLs.
 *
 * When `next` is not encoded, URLSearchParams truncates its value at the first
 * `&` because it treats it as a parameter separator. This function detects that
 * case and falls back to extracting everything after `next=` from the raw string.
 *
 * Only relative paths (starting with `/`) are allowed to prevent open redirects.
 */
export function getNextParam(rawSearch: string): { next: string } | Record<string, never> {
  const search = rawSearch.startsWith('?') ? rawSearch.slice(1) : rawSearch;
  const params = new URLSearchParams(search);
  const parsedNext = params.get('next');

  if (parsedNext === null) return {};

  const match = search.match(/(?:^|&)next=(.*)/);
  if (!match) return {};

  const rawValue = match[1];

  // If the raw value starts with `/`, the URL was NOT percent-encoded.
  // URLSearchParams likely truncated at an `&` that was part of the value.
  // Use the full raw value instead.
  //
  // If it starts with `%` (e.g. `%2F`), the URL WAS encoded and
  // URLSearchParams decoded it correctly — trust its result.
  const next = rawValue.startsWith('/') ? rawValue : parsedNext;

  if (!next.startsWith('/') || next.startsWith('//')) return {};

  return { next };
}
